import type { SendExpertAssistMessage } from "@expert/shared-types";
import type { JSX } from "react";

import { useReactAnalytics } from "@asurion-core/eventualize-react";
import { Button } from "@mantine/core";

import type { UnsupportedDeviceAssistPromptType } from "./unsupportedDeviceAssist";

import classes from "./UnsupportedDeviceAssistButton.module.css";

interface UnsupportedDeviceAssistButtonProps {
    callSid?: string;
    extraData?: Record<string, unknown>;
    /** The (verbose) question sent to AI Assist. Hidden from the expert behind the "Activation Assist"
     *  chip via `helpMeNowText`. */
    prompt: string;
    /** How `prompt`'s device context was resolved — reported to analytics for the demand signal. */
    promptType: UnsupportedDeviceAssistPromptType;
    /** Device make/model when known (PII-safe) — sent to analytics so we can see which unsupported
     *  devices drive volume. Absent for the transcript-inference case. */
    deviceMake?: string;
    deviceModel?: string;
    sendMessage: SendExpertAssistMessage;
}

/** Shown to the expert in the chat bubble in place of the verbose prompt (the "invisible prompt"). */
const ACTIVATION_ASSIST_LABEL = "Activation Assist";

/**
 * The AI Assist handoff CTA shown on the device-activation "device not supported" step. Distinct from
 * the shared {@link AskAssist} affordance (which any Simplr step opts into via `assistPrompt`): this is
 * a prominent button rendered next to the guide's action buttons. On click it sends a verbose,
 * device-aware question to AI Assist — displayed to the expert only as an "Activation Assist" chip
 * (`helpMeNowText`); the answer appears in the AI Assist conversation below the guide.
 */
export function UnsupportedDeviceAssistButton({
    callSid,
    extraData,
    prompt,
    promptType,
    deviceMake,
    deviceModel,
    sendMessage,
}: UnsupportedDeviceAssistButtonProps): JSX.Element {
    const { dispatcher } = useReactAnalytics();

    const enhancedDispatcher = callSid ? dispatcher.withIdentities({ CallSid: callSid }) : dispatcher;

    const onClick = () => {
        void enhancedDispatcher.dispatchBusinessEvent(`Click_Guide_AskAssist`, {
            // Distinct from the shared `AskAssist` component's `button: "AskAssist"` so this CTA is
            // unambiguously attributable in analytics (the unsupported-device demand signal). make/model
            // are device attributes (PII-safe); the verbose prompt text is intentionally NOT sent here.
            button: "UnsupportedDeviceAskAssist",
            promptType,
            deviceMake,
            deviceModel,
            ...extraData,
        });
        sendMessage({
            text: prompt,
            type: "Expert",
            source: "GuideAskAssist",
            // Show a short chip to the expert; the full `text` above still goes to the bot.
            metadata: { helpMeNowText: ACTIVATION_ASSIST_LABEL },
        });
    };

    return (
        <Button className={classes.askAssistButton} fz="xs" miw="5.5rem" onClick={onClick} size="xs">
            Ask Assist
        </Button>
    );
}
